import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, push, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    ConnectionLineType,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import EmailNode from './EmailNodes/EmailNode';
import TriggerEmailNode from './EmailNodes/TriggerEmailNode';
import DelayNode from './EmailNodes/DelayNode';
import ConditionEmailNode from './EmailNodes/ConditionEmailNode';
import EmailNodeSidebar from './EmailNodeSidebar';
import FlowSequencePreview from './FlowSequencePreview';
import './EmailFlowBuilder.css';

const nodeTypes = {
    email: EmailNode,
    trigger: TriggerEmailNode,
    delay: DelayNode,
    condition: ConditionEmailNode,
};

const EmailFlowBuilder = ({ onClose, onFlowCreated, editMode = false, existingFlow = null }) => {
    const navigate = useNavigate();
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    const [nodes, setNodes, onNodesChange] = useNodesState(existingFlow?.nodes || []);
    const [edges, setEdges, onEdgesChange] = useEdgesState(existingFlow?.edges || []);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showSidebar, setShowSidebar] = useState(false);
    const [flowName, setFlowName] = useState(existingFlow?.name || '');
    const [flowDescription, setFlowDescription] = useState(existingFlow?.description || '');
    const [saving, setSaving] = useState(false);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [setEdges]
    );

    const addEmailNode = useCallback((type) => {
        const newNode = {
            id: `${type}-${Date.now()}`,
            type: type,
            position: { 
                x: Math.random() * 400 + 100, 
                y: Math.random() * 300 + 100 
            },
            data: getDefaultEmailNodeData(type),
        };

        setNodes((nds) => nds.concat(newNode));
    }, [setNodes]);

    const getDefaultEmailNodeData = (type) => {
        switch (type) {
            case 'trigger':
                return {
                    label: 'Flow Start',
                    triggerType: 'flow_start', // Simplified trigger type
                    config: {}
                };
            case 'email':
                return {
                    label: 'Welcome Email',
                    subject: 'Welcome!',
                    templateId: null,
                    config: {}
                };
            case 'delay':
                return {
                    label: 'Wait 24 hours',
                    delayType: 'hours',
                    delayAmount: 24,
                    config: {}
                };
            case 'condition':
                return {
                    label: 'Check Email Opened',
                    conditionType: 'email_opened',
                    config: {}
                };
            default:
                return { label: 'Node' };
        }
    };

    const handleNodeClick = useCallback((event, node) => {
        setSelectedNode(node);
        setShowSidebar(true);
    }, []);

    const updateNodeData = useCallback((nodeId, newData) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId
                    ? { ...node, data: { ...node.data, ...newData } }
                    : node
            )
        );
    }, [setNodes]);

    const handleSaveFlow = async () => {
        if (!currentUser || !flowName.trim()) {
            alert('Please enter a flow name');
            return;
        }

        setSaving(true);
        try {
            const flowData = {
                userId: currentUser.uid,
                name: flowName.trim(),
                description: flowDescription.trim(),
                nodes: nodes,
                edges: edges,
                status: 'inactive', // Default status
                emailCount: nodes.filter(node => node.type === 'email').length,
                triggerCount: nodes.filter(node => node.type === 'trigger').length,
                createdAt: Date.now(),
                updatedAt: Date.now()
            };

            if (editMode && existingFlow) {
                // Update existing flow
                await set(ref(db, `emailFlows/${existingFlow.id}`), {
                    ...flowData,
                    createdAt: existingFlow.createdAt // Keep original creation date
                });
                onFlowCreated(existingFlow.id);
            } else {
                // Create new flow
                const newFlowRef = push(ref(db, 'emailFlows'));
                await set(newFlowRef, flowData);
                onFlowCreated(newFlowRef.key);
            }
        } catch (error) {
            console.error('Error saving flow:', error);
            alert('Error saving flow. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="email-flow-builder-modal">
            <div className="email-flow-builder-content">
                <div className="email-flow-builder-header">
                    <div className="email-flow-info">
                        <input
                            type="text"
                            value={flowName}
                            onChange={(e) => setFlowName(e.target.value)}
                            placeholder="Enter flow name..."
                            className="email-flow-name-input"
                        />
                        <input
                            type="text"
                            value={flowDescription}
                            onChange={(e) => setFlowDescription(e.target.value)}
                            placeholder="Flow description..."
                            className="email-flow-description-input"
                        />
                    </div>
                    
                    <div className="email-flow-header-actions">
                        <button 
                            className="email-flow-save-btn" 
                            onClick={handleSaveFlow}
                            disabled={saving}
                        >
                            {saving ? '💾 Saving...' : '💾 Save Flow'}
                        </button>
                        <button className="email-flow-close-btn" onClick={onClose}>
                            ✕
                        </button>
                    </div>
                </div>

                <div className="email-flow-main">
                    <div className="email-flow-palette">
                        <h5>Add Nodes</h5>
                        
                        {/* Sequence Preview */}
                        <FlowSequencePreview nodes={nodes} edges={edges} />
                        
                        <div className="email-node-category">
                            <h6>🚀 Flow Start</h6>
                            <button
                                className="email-node-btn trigger"
                                onClick={() => addEmailNode('trigger')}
                            >
                                <span className="email-node-icon">🚀</span>
                                <span>Flow Start</span>
                            </button>
                        </div>

                        <div className="email-node-category">
                            <h6>📧 Emails</h6>
                            <button
                                className="email-node-btn email"
                                onClick={() => addEmailNode('email')}
                            >
                                <span className="email-node-icon">📧</span>
                                <span>Send Email</span>
                            </button>
                        </div>

                        <div className="email-node-category">
                            <h6>⏰ Timing</h6>
                            <button
                                className="email-node-btn delay"
                                onClick={() => addEmailNode('delay')}
                            >
                                <span className="email-node-icon">⏰</span>
                                <span>Wait/Delay</span>
                            </button>
                        </div>

                        <div className="email-node-category">
                            <h6>❓ Conditions</h6>
                            <button
                                className="email-node-btn condition"
                                onClick={() => addEmailNode('condition')}
                            >
                                <span className="email-node-icon">🔍</span>
                                <span>Check Condition</span>
                            </button>
                        </div>
                    </div>

                    <div className="email-flow-editor">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onNodeClick={handleNodeClick}
                            nodeTypes={nodeTypes}
                            connectionLineType={ConnectionLineType.SmoothStep}
                            fitView
                            className="email-react-flow"
                        >
                            <Controls className="email-flow-controls" />
                            <MiniMap className="email-flow-minimap" />
                            <Background variant="dots" gap={20} size={1} />
                            
                            <Panel position="top-center" className="email-flow-panel">
                                <div className="email-flow-status">
                                    <span>{nodes.length} nodes, {edges.length} connections</span>
                                </div>
                            </Panel>
                        </ReactFlow>
                    </div>

                    {showSidebar && selectedNode && (
                        <EmailNodeSidebar
                            node={selectedNode}
                            onClose={() => setShowSidebar(false)}
                            onUpdateNode={updateNodeData}
                            onEditEmail={(emailId) => navigate(`/emailflow/template/${emailId}`)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailFlowBuilder;
